import React, { useState, useEffect } from 'react';
import T from '../../../components/common/T';
import { useNavigate } from 'react-router-dom';
import { useUsers } from '../../../context/UserContext'; 
import { useRoles } from '../../../context/RoleContext'; 
import { toast } from 'react-toastify';
import { UserCircleIcon, EnvelopeIcon, BriefcaseIcon, LockClosedIcon } from '@heroicons/react/24/outline';

// 🟢 SECURITY IMPORTS
import { useAuth } from '../../../context/AuthContext';
import { SCOPES } from '../../../config/permissions';

const AddUser = () => {
  const navigate = useNavigate();
  const { addUser } = useUsers();
  
  // 🟢 Fetch Real Roles from Database
  const { roles = [], fetchRoles } = useRoles(); 
  const { checkAccess } = useAuth(); 

  const defaultRoles = [
    { id: 1, name: "Super Admin" },
    { id: 2, name: "Project Manager" },
    { id: 3, name: "Site Engineer" },
    { id: 4, name: "Procurement Officer" },
    { id: 5, name: "Safety Officer (HSE)" },
    { id: 6, name: "Customer" },
    { id: 7, name: "Worker" }
  ];
  const availableRoles = roles && roles.length > 0 ? roles : defaultRoles;

  // 🟢 PAGE GUARD
  useEffect(() => {
    if (!checkAccess(SCOPES.CREATE_USER)) {
        toast.error("Access Denied: You cannot create users.");
        navigate('/admin/users');
    }
    // Ensure roles are loaded for the dropdown
    if (typeof fetchRoles === 'function') {
      fetchRoles();
    }
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '', // Stores Role ID or Role Name
    status: 'Active',
    password: '',
    bio: '',
    location: '',
    phone: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if(!formData.role) return toast.warning("Please select a role");

    setSubmitting(true);
    try {
      const success = await addUser(formData);
      if (success) {
        toast.success("User created successfully!");
        navigate('/admin/users');
      }
    } catch (err) {
      console.error("Create User submission failed:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-4xl mx-auto bg-white dark:bg-[#161b22] rounded-2xl shadow-xl border border-gray-100 dark:border-[#30363d] overflow-hidden transition-colors">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 sm:px-8 py-6">
          <h2 className="text-lg font-bold text-white flex items-center gap-3">
            <UserCircleIcon className="h-8 w-8 text-blue-200" />
            Create New User
          </h2>
          <p className="text-blue-100 text-xs sm:text-sm mt-1 ml-11"><T>Add a new member to the system and assign their role.</T></p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 sm:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            
            {/* Left Column */}
            <div className="space-y-5">
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"><T>Full Name</T></label>
                <div className="relative">
                  <UserCircleIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input type="text" name="name" required 
                    className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#f0f6fc] border border-gray-300 dark:border-[#30363d] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition"
                    placeholder="John Doe" value={formData.name} onChange={handleChange} />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"><T>Email Address</T></label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input type="email" name="email" required 
                    className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#f0f6fc] border border-gray-300 dark:border-[#30363d] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition"
                    placeholder="john@example.com" value={formData.email} onChange={handleChange} />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"><T>Password</T></label>
                <div className="relative">
                  <LockClosedIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <input type="password" name="password" required 
                    className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#f0f6fc] border border-gray-300 dark:border-[#30363d] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm transition"
                    placeholder="******" value={formData.password} onChange={handleChange} />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-5">
              
              {/* 🟢 REAL ROLE DROPDOWN */}
              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"><T>Assign Role</T></label>
                <div className="relative">
                  <BriefcaseIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400 pointer-events-none" />
                  <select 
                    name="role" 
                    required 
                    value={formData.role}
                    onChange={handleChange}
                    className="w-full pl-10 pr-8 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#f0f6fc] border border-gray-300 dark:border-[#30363d] rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm cursor-pointer transition"
                  >
                    <option value=""><T>Select a Role</T></option>
                    {availableRoles.map(r => (
                        <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                 <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"><T>Status</T></label>
                 <select name="status" value={formData.status} onChange={handleChange} className="w-full px-4 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#f0f6fc] border border-gray-300 dark:border-[#30363d] rounded-lg text-sm cursor-pointer transition">
                    <option value="Active"><T>Active</T></option>
                    <option value="Inactive"><T>Inactive</T></option>
                 </select>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5"><T>Location</T></label>
                <input type="text" name="location" 
                    className="w-full px-4 py-2.5 bg-white dark:bg-[#0d1117] text-gray-900 dark:text-[#f0f6fc] border border-gray-300 dark:border-[#30363d] rounded-lg text-sm transition"
                    placeholder="e.g. New York Office" value={formData.location} onChange={handleChange} />
              </div>

            </div>
          </div>

          <div className="mt-8 flex justify-end gap-3 sm:gap-4 border-t border-gray-100 dark:border-[#30363d] pt-6">
            <button type="button" onClick={() => navigate('/admin/users')}
              className="px-6 py-2.5 bg-gray-100 dark:bg-[#21262d] text-gray-700 dark:text-gray-200 font-semibold rounded-lg hover:bg-gray-200 dark:hover:bg-[#30363d] text-sm transition">
              Cancel
            </button>
            <button type="submit"
              disabled={submitting}
              className="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold rounded-lg shadow-lg hover:shadow-xl text-sm transition cursor-pointer">
              {submitting ? "Creating User..." : "Create User"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUser;